import { api } from "@/lib/apiClient";
import { seedCatalogIfEmpty } from "@/lib/catalog/catalogSeed";
import type {
  Attribute,
  AttributeGroup,
  AttributeOption,
  Category,
  CategoryProductSchema,
  ID,
  UploadedFile,
  UploadVisibility,
} from "@/lib/catalog/types";
import { normalizeVisibilityRule } from "@/lib/catalog/attributeVisibility";

function ensureLoaded() {
  seedCatalogIfEmpty();
}

/** Laravel schema endpoint uses snake_case; UI expects camelCase (types + sortByOrder). */
function normalizeCategoryProductSchema(raw: unknown): CategoryProductSchema {
  if (!raw || typeof raw !== "object") {
    return { category: { id: "", name: "" }, groups: [] };
  }
  const o = raw as Record<string, unknown>;
  const catRaw = o.category;
  const category: Category =
    catRaw && typeof catRaw === "object"
      ? (() => {
          const c = catRaw as Record<string, unknown>;
          const pid = c.parent_id ?? c.parentId;
          return {
            id: String(c.id ?? ""),
            name: String(c.name ?? ""),
            parentId: pid == null || pid === "" ? null : String(pid),
          };
        })()
      : { id: "", name: "" };

  const groups: AttributeGroup[] = [];
  const groupsRaw = o.groups;
  if (Array.isArray(groupsRaw)) {
    for (const g of groupsRaw) {
      if (!g || typeof g !== "object") continue;
      const gr = g as Record<string, unknown>;
      const attributes: Attribute[] = [];
      const attrsRaw = gr.attributes;
      if (Array.isArray(attrsRaw)) {
        for (const a of attrsRaw) {
          if (!a || typeof a !== "object") continue;
          const ar = a as Record<string, unknown>;
          const options: AttributeOption[] = [];
          const optsRaw = ar.options;
          if (Array.isArray(optsRaw)) {
            for (const opt of optsRaw) {
              if (!opt || typeof opt !== "object") continue;
              const op = opt as Record<string, unknown>;
              options.push({
                id: String(op.id ?? ""),
                value: String(op.value ?? ""),
                label: String(op.label ?? ""),
                sortOrder: Number(op.sort_order ?? op.sortOrder ?? 0),
              });
            }
          }
          attributes.push({
            id: String(ar.id ?? ""),
            code: String(ar.code ?? ""),
            name: String(ar.name ?? ""),
            type: ar.type as Attribute["type"],
            isRequired: Boolean(ar.is_required ?? ar.isRequired),
            isListing: Boolean(ar.is_listing ?? ar.isListing),
            isFilter: Boolean(ar.is_filter ?? ar.isFilter),
            isInvoice: Boolean(ar.is_invoice ?? ar.isInvoice),
            isDeliveryChecklist: Boolean(ar.is_delivery_checklist ?? ar.isDeliveryChecklist),
            isGatePass: Boolean(ar.is_gate_pass ?? ar.isGatePass),
            allowRuntimeAdd: Boolean(ar.allow_runtime_add ?? ar.allowRuntimeAdd),
            sortOrder: Number(ar.sort_order ?? ar.sortOrder ?? 0),
            parentAttributeId:
              ar.parent_attribute_id == null || ar.parent_attribute_id === ""
                ? null
                : String(ar.parent_attribute_id ?? ar.parentAttributeId ?? ""),
            visibilityRule: normalizeVisibilityRule(ar.visibility_rule ?? ar.visibilityRule),
            options,
            accept: ar.accept == null || ar.accept === "" ? undefined : String(ar.accept),
            maxSizeMb:
              ar.max_size_mb != null && ar.max_size_mb !== ""
                ? Number(ar.max_size_mb)
                : ar.maxSizeMb != null
                  ? Number(ar.maxSizeMb)
                  : undefined,
            maxFiles:
              ar.max_files != null && ar.max_files !== ""
                ? Number(ar.max_files)
                : ar.maxFiles != null
                  ? Number(ar.maxFiles)
                  : undefined,
          });
        }
      }
      groups.push({
        id: String(gr.id ?? ""),
        name: String(gr.name ?? ""),
        slug: String(gr.slug ?? ""),
        description: gr.description == null ? null : String(gr.description),
        sortOrder: Number(gr.sort_order ?? gr.sortOrder ?? 0),
        attributes,
      });
    }
  }

  return { category, groups };
}

export async function listCategories(): Promise<Category[]> {
  ensureLoaded();
  const response = await api.get<{ data?: Array<{ id: string | number; name: string; parent_id?: string | number | null }> }>(
    "/api/v1/catalog/categories",
    { params: { page: 1, per_page: 200 } },
  );
  return (response.data?.data ?? []).map((c) => ({
    id: String(c.id),
    name: c.name,
    parentId: c.parent_id == null ? null : String(c.parent_id),
  }));
}

export async function getCategoryProductSchema(categoryId: ID): Promise<CategoryProductSchema> {
  ensureLoaded();
  const response = await api.get<unknown>(`/api/v1/catalog/categories/${categoryId}/schema`);
  return normalizeCategoryProductSchema(response.data);
}

function isImage(mimeType: string) {
  return mimeType.toLowerCase().startsWith("image/");
}

function inferVisibility(attribute: Attribute): UploadVisibility {
  if (attribute.type === "image" || attribute.type === "images") return "public";
  return "private";
}

export async function mockUploadFile(file: File, attribute: Attribute): Promise<UploadedFile> {
  const visibility = inferVisibility(attribute);
  return {
    id: crypto.randomUUID(),
    filename: file.name,
    mimeType: file.type || (isImage(file.type) ? "image/*" : "application/octet-stream"),
    size: file.size,
    visibility,
    url: visibility === "public" ? URL.createObjectURL(file) : undefined,
    localFile: file,
  };
}
